import { isTriggerType, isTv2mSource } from '@apm/contracts';
import type { Tv2mLinkContext } from '@apm/contracts';

/**
 * 마지막으로 열었던 상품 컨텍스트를 앱이 스스로 보관한다.
 *
 * ── 이것이 갚는 부채 ──────────────────────────────────────────
 *
 * T5 가 남긴 것: **Tier 0 의 Deferred Link 는 지문으로 복원되지 않는다.**
 * `MatchSession.persist` 가 지문 재료를 채우지 않으므로(트리거를 부른 것은 TV 다)
 * Tier 0 링크의 항목은 `hashedIp` 뿐이고 점수가 0 이다. 토큰으로만 열리는데 그 토큰은
 * Tier 0 응답에 실리지 않는다(QR 이 아니라 내보낼 자리가 없다). 즉 `docs/07` §4 의
 * 시나리오 A-6 — "설치 안내를 거쳐 복원" — 이 서버만으로는 성립하지 않는다.
 *
 * T5 가 적어 둔 고칠 곳은 둘이었다. 트리거 시점에 UA 를 저장하거나(T3 코드 변경),
 * **앱이 설치 전에 Deep Link 컨텍스트를 스스로 보관하거나.** 후자를 택했다 —
 * Tier 0 은 이미 같은 기기이고 같은 오리진이라 **서버에 물어볼 필요가 애초에 없다.**
 * 저장소가 그 자리에 있고, 그것이 A2HS 설치 전후로 유지된다.
 *
 * 서버 경로를 대체하지 않는다. QR 경로(Tier 4)는 스캔한 기기에 아무것도 없는 상태로
 * 시작하므로 `deferred-link/check` 가 유일한 길이다. 이것은 **같은 기기가 이미 본 것을
 * 잊지 않는** 것뿐이다.
 */

const KEY = 'tv2m.lastContext';

export function saveLastContext(context: Tv2mLinkContext): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(context));
  } catch {
    // 저장소를 못 쓰는 것이 화면을 막을 이유는 아니다 (`LocalStorageAdapter` 와 같은 방침).
  }
}

/**
 * 보관해 둔 컨텍스트. 없거나 모양이 어긋나면 `null`.
 *
 * **되읽을 때 다시 검증한다.** 저장한 것은 우리지만 그 사이에 계약이 바뀌었을 수 있고
 * (`Tv2mSource` 값이 늘거나 줄거나), 모양이 어긋난 것을 그대로 화면에 넘기면 상품 화면이
 * 빈 값으로 그려진다.
 */
export function loadLastContext(): Tv2mLinkContext | null {
  let raw: unknown;
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === null) return null;
    raw = JSON.parse(stored);
  } catch {
    return null;
  }

  if (typeof raw !== 'object' || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const { productId, campaignId, source, triggerType } = value;

  if (typeof productId !== 'string' || productId === '') return null;
  if (typeof campaignId !== 'string' || campaignId === '') return null;
  if (!isTv2mSource(source) || !isTriggerType(triggerType)) return null;

  const context: Tv2mLinkContext = { productId, campaignId, source, triggerType };
  if (typeof value['matchId'] === 'string') context.matchId = value['matchId'];
  if (typeof value['channelId'] === 'string') context.channelId = value['channelId'];
  if (typeof value['contentId'] === 'string') context.contentId = value['contentId'];
  if (typeof value['coupon'] === 'string') context.coupon = value['coupon'];
  return context;
}
